import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  ALL_ROLES,
  MAX_PROFILES,
  canAddProfile,
  isRole,
  normalizeRoleLabels,
  normalizeRolePrefs,
  normalizeRoleList,
  prefsFromProfileRow,
  profilePatchFromPrefs,
  removeProfile as removeProfileState,
  setRolePrefs,

  type AccountRole,
  type RoleLabels,
  type RolePrefs,
  type RolePrefsMap,
} from "@/lib/role-access";

export type AccountType = AccountRole;

export const ACCOUNT_TYPES: AccountType[] = ALL_ROLES;

/** Massimo di profili attivabili su uno stesso account (solo maggiorenni). */
export const MAX_ROLES = MAX_PROFILES;

export const isAccountType = isRole;

/** Normalizza i ruoli scelti: massimo 2 e solo se maggiorenne. */
export function normalizeRoles(roles: string[], isAdult: boolean, active?: string): AccountType[] {
  return normalizeRoleList(roles, isAdult, active);
}

export type AccountProfile = {
  /** Ruolo attualmente in uso: l'interfaccia si adatta a questo. */
  active: AccountType;
  /** Tutti i profili dell'account (max 2). */
  roles: AccountType[];
  isAdult: boolean;
  /** Nomi personalizzati dei profili. */
  labels: RoleLabels;
  /** Profilo usato di default all'accesso. */
  defaultRole: AccountType;
  /** Preferenze separate per ciascun profilo. */
  prefs: RolePrefsMap;
  loading: boolean;
};

const FALLBACK: AccountProfile = {
  active: "student",
  roles: ["student"],
  isAdult: false,
  labels: {},
  defaultRole: "student",
  prefs: {},
  loading: false,
};

/** Profili dell'account: ruolo attivo, ruoli disponibili, nomi e preferenze. */
export function useAccountProfile(): AccountProfile {
  const q = useQuery({
    queryKey: ["account-type"],
    queryFn: async (): Promise<AccountProfile> => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return FALLBACK;
      const { data } = await supabase
        .from("profiles")
        .select("account_type,account_roles,is_adult,role_labels,role_prefs,default_role")
        .eq("id", u.user.id)
        .maybeSingle();
      const row = (data ?? {}) as Record<string, unknown>;
      const isAdult = Boolean(row["is_adult"]);
      const roles = normalizeRoleList(row["account_roles"], isAdult, row["account_type"]);
      const raw = row["account_type"];
      const active = isRole(raw) && roles.includes(raw) ? raw : roles[0]!;
      const rawDefault = row["default_role"];
      const defaultRole = isRole(rawDefault) && roles.includes(rawDefault) ? rawDefault : active;
      return {
        active,
        roles,
        isAdult,
        labels: normalizeRoleLabels(row["role_labels"]),
        defaultRole,
        prefs: normalizeRolePrefs(row["role_prefs"]),
        loading: false,
      };
    },
    staleTime: 60_000,
  });
  return q.data ?? { ...FALLBACK, loading: q.isLoading };
}

/** Legge il tipo di account attivo (studente / docente / genitore). */
export function useAccountType(): AccountType {
  return useAccountProfile().active;
}

async function updateProfile(patch: Record<string, unknown>) {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) throw new Error("Not signed in");
  const { error } = await supabase.from("profiles").update(patch as never).eq("id", u.user.id);
  if (error) throw new Error(error.message);
}

/**
 * Cambia il profilo attivo: prima "fotografa" le impostazioni correnti nel
 * profilo che si lascia, poi riapplica quelle del profilo scelto, così lingua,
 * accessibilità, stile di studio e notifiche restano separate per ciascuno.
 */
export function useSwitchRole() {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  return useCallback(
    async (role: AccountType) => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new Error("Not signed in");
      const { data } = await supabase
        .from("profiles")
        .select(
          "account_type,role_prefs,language,dsa_mode_enabled,dsa_intensity,focus_mode_enabled,learning_style,notif_prefs",
        )
        .eq("id", u.user.id)
        .maybeSingle();
      const row = (data ?? {}) as Record<string, unknown>;
      const current = isRole(row["account_type"]) ? (row["account_type"] as AccountType) : role;
      const map = setRolePrefs(normalizeRolePrefs(row["role_prefs"]), current, prefsFromProfileRow(row));
      const patch = { account_type: role, role_prefs: map, ...profilePatchFromPrefs(map[role] ?? {}) };
      await updateProfile(patch);
      const lang = map[role]?.language;
      if (lang && lang !== i18n.language) await i18n.changeLanguage(lang);
      await queryClient.invalidateQueries();
    },
    [queryClient, i18n],
  );
}


export type ProfileManagerApi = {
  addProfile: (role: AccountType) => Promise<void>;
  removeProfile: (role: AccountType) => Promise<void>;
  renameProfile: (role: AccountType, name: string) => Promise<void>;
  setDefaultProfile: (role: AccountType) => Promise<void>;
  setAdult: (adult: boolean) => Promise<void>;
  savePrefs: (role: AccountType, patch: RolePrefs) => Promise<void>;
};

/** Azioni di gestione profili (aggiunta, rimozione, rinomina, default). */
export function useProfileManager(): ProfileManagerApi {
  const queryClient = useQueryClient();
  const account = useAccountProfile();
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries();
  }, [queryClient]);

  const addProfile = useCallback(
    async (role: AccountType) => {
      const check = canAddProfile(account.roles, account.isAdult, role);
      if (!check.ok) throw new Error(check.reason);
      await updateProfile({ account_roles: [...account.roles, role] });
      await refresh();
    },
    [account.roles, account.isAdult, refresh],
  );

  const removeProfile = useCallback(
    async (role: AccountType) => {
      const next = removeProfileState(
        {
          roles: account.roles,
          labels: account.labels,
          prefs: account.prefs,
          active: account.active,
          defaultRole: account.defaultRole,
        },
        role,
      );
      if (next.roles.length === account.roles.length) throw new Error("last_profile");
      await updateProfile({
        account_roles: next.roles,
        role_labels: next.labels,
        role_prefs: next.prefs,
        account_type: next.active,
        default_role: next.defaultRole,
      });
      await refresh();
    },
    [account, refresh],
  );

  const renameProfile = useCallback(
    async (role: AccountType, name: string) => {
      const labels = { ...account.labels };
      const clean = name.trim().slice(0, 40);
      if (clean) labels[role] = clean;
      else delete labels[role];
      await updateProfile({ role_labels: labels });
      await refresh();
    },
    [account.labels, refresh],
  );

  const setDefaultProfile = useCallback(
    async (role: AccountType) => {
      await updateProfile({ default_role: role });
      await refresh();
    },
    [refresh],
  );

  const setAdult = useCallback(
    async (adult: boolean) => {
      const roles = adult ? account.roles : account.roles.slice(0, 1);
      await updateProfile({ is_adult: adult, account_roles: roles });
      await refresh();
    },
    [account.roles, refresh],
  );

  const savePrefs = useCallback(
    async (role: AccountType, patch: RolePrefs) => {
      await updateProfile({ role_prefs: setRolePrefs(account.prefs, role, patch) });
      await refresh();
    },
    [account.prefs, refresh],
  );

  return { addProfile, removeProfile, renameProfile, setDefaultProfile, setAdult, savePrefs };
}

/**
 * Traduzione consapevole del ruolo: prova prima `roleui.<ruolo>.<chiave>`
 * e ricade sulla chiave normale (es. "Tutor AI" → "Assistente" per i prof).
 */
export function useRoleT(): (key: string, opts?: Record<string, unknown>) => string {
  const { t } = useTranslation();
  const role = useAccountType();
  return useCallback(
    (key: string, opts?: Record<string, unknown>) =>
      t(`roleui.${role}.${key}`, { ...(opts ?? {}), defaultValue: t(key, opts ?? {}) }) as string,
    [t, role],
  );
}

/** Nome visualizzato del profilo: personalizzato se impostato, altrimenti il ruolo. */
export function useRoleName(): (role: AccountType) => string {
  const { t } = useTranslation();
  const { labels } = useAccountProfile();
  return useCallback((role: AccountType) => labels[role] ?? (t(`teacher.role.${role}`) as string), [labels, t]);
}
